<template>
  <div class="policy-modern">
    <div class="hero-section">
      <div class="hero-overlay"></div>
      <div class="container">
        <el-breadcrumb class="breadcrumb">
          <el-breadcrumb-item
            ><span class="breadcrumb_span" @click="backHome"
              >政务服务</span
            ></el-breadcrumb-item
          >
          <el-breadcrumb-item>政策查询</el-breadcrumb-item>
        </el-breadcrumb>
        <h1 class="hero-title">再生资源政策法规</h1>
        <p class="hero-subtitle">权威发布 · 分类解读 · 便捷查询</p>
        <div class="hero-search">
          <el-input
            v-model="searchQuery"
            placeholder="搜索政策法规关键词"
            class="search-input"
            @keyup.enter.native="handleSearch"
          >
            <el-button
              slot="append"
              icon="el-icon-search"
              @click="handleSearch"
            ></el-button>
          </el-input>
          <div class="search-tags">
            <el-tag
              v-for="tag in hotTags"
              :key="tag"
              size="medium"
              @click="searchByTag(tag)"
              >{{ tag }}</el-tag
            >
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="content-layout">
        <!-- 左侧主内容 -->
        <div class="main-content">
          <!-- 政策筛选 -->
          <el-card shadow="never" class="filter-card glassmorph">
            <div class="filter-header">
              <h2>政策筛选</h2>
              <el-button type="text" @click="resetFilters" class="reset-btn"
                >重置筛选</el-button
              >
            </div>
            <div class="filter-body">
              <el-row :gutter="20">
                <el-col :span="8">
                  <div class="filter-item">
                    <h3>政策类型</h3>
                    <el-select
                      v-model="filter.type"
                      placeholder="全部类型"
                      clearable
                    >
                      <el-option
                        v-for="item in policyTypes"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      >
                      </el-option>
                    </el-select>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="filter-item">
                    <h3>发布部门</h3>
                    <el-select
                      v-model="filter.department"
                      placeholder="全部部门"
                      clearable
                    >
                      <el-option
                        v-for="item in departments"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      >
                      </el-option>
                    </el-select>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="filter-item">
                    <h3>发布时间</h3>
                    <el-date-picker
                      v-model="filter.dateRange"
                      type="daterange"
                      range-separator="至"
                      start-placeholder="开始日期"
                      end-placeholder="结束日期"
                      value-format="yyyy-MM-dd"
                    >
                    </el-date-picker>
                  </div>
                </el-col>
              </el-row>
            </div>
          </el-card>

          <!-- 政策列表 -->
          <el-card shadow="never" class="list-card glassmorph">
            <div class="list-header">
              <h2>政策法规列表</h2>
              <div class="sort-options">
                <span>排序方式：</span>
                <el-radio-group v-model="sortOption" size="small">
                  <el-radio-button label="date">发布时间</el-radio-button>
                  <el-radio-button label="hot">热度</el-radio-button>
                  <el-radio-button label="level">效力级别</el-radio-button>
                </el-radio-group>
              </div>
            </div>

            <div class="policy-list">
              <div
                class="policy-item"
                v-for="policy in filteredPolicies"
                :key="policy.id"
                @click="showPolicyDetail(policy)"
              >
                <div class="policy-badge" :style="getBadgeStyle(policy.level)">
                  {{ policy.level }}
                </div>
                <div class="policy-content">
                  <h3 class="policy-title">
                    {{ policy.title }}
                    <el-tag
                      v-if="policy.isNew"
                      size="mini"
                      type="danger"
                      effect="dark"
                      >新</el-tag
                    >
                  </h3>
                  <div class="policy-meta">
                    <span class="meta-item"
                      ><i class="el-icon-office-building"></i>
                      {{ policy.department }}</span
                    >
                    <span class="meta-item"
                      ><i class="el-icon-date"></i> {{ policy.date }}</span
                    >
                    <span class="meta-item"
                      ><i class="el-icon-document"></i> {{ policy.type }}</span
                    >
                  </div>
                  <p class="policy-summary">{{ policy.summary }}</p>
                  <div class="policy-tags">
                    <el-tag
                      v-for="tag in policy.tags"
                      :key="tag"
                      size="mini"
                      type="info"
                      >{{ tag }}</el-tag
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="pagination">
              <el-pagination
                :current-page="currentPage"
                :page-size="pageSize"
                :total="totalPolicies"
                layout="prev, pager, next, jumper"
                @current-change="handlePageChange"
              >
              </el-pagination>
            </div>
          </el-card>
        </div>

        <!-- 右侧边栏 -->
        <div class="sidebar">
          <!-- 政策分类 -->
          <el-card shadow="never" class="category-card glassmorph">
            <div class="card-header">
              <div class="header-icon">
                <i class="el-icon-s-grid"></i>
              </div>
              <h2>政策分类</h2>
            </div>
            <div class="category-list">
              <div
                class="category-item"
                v-for="category in policyCategories"
                :key="category.id"
                :class="{ active: activeCategory === category.id }"
                @click="filterByCategory(category.id)"
              >
                <div class="category-icon">
                  <i :class="category.icon"></i>
                </div>
                <div class="category-info">
                  <h3>{{ category.name }}</h3>
                  <p>{{ category.count }}条相关政策</p>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 最新政策 -->
          <el-card shadow="never" class="latest-card glassmorph">
            <div class="card-header">
              <div class="header-icon">
                <i class="el-icon-s-opportunity"></i>
              </div>
              <h2>最新政策</h2>
            </div>
            <div class="latest-list">
              <div
                class="latest-item"
                v-for="item in latestPolicies"
                :key="item.id"
                @click="showPolicyDetail(item)"
              >
                <div class="latest-date">{{ item.date }}</div>
                <div class="latest-title">{{ item.title }}</div>
              </div>
            </div>
          </el-card>

          <!-- 政策解读 -->
          <el-card shadow="never" class="interpretation-card glassmorph">
            <div class="card-header">
              <div class="header-icon">
                <i class="el-icon-s-comment"></i>
              </div>
              <h2>政策解读</h2>
            </div>
            <div class="interpretation-list">
              <div
                class="interpretation-item"
                v-for="item in interpretations"
                :key="item.id"
              >
                <div class="interpretation-image">
                  <img :src="item.image" :alt="item.title" />
                </div>
                <div class="interpretation-content">
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.summary }}</p>
                  <el-button type="text" @click="viewInterpretation(item)"
                    >查看详情</el-button
                  >
                </div>
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>

    <!-- 政策详情弹窗 -->
    <el-dialog
      :title="currentPolicy.title"
      :visible.sync="dialogVisible"
      width="80%"
      top="5vh"
      custom-class="policy-dialog"
    >
      <div class="policy-detail">
        <div class="detail-header">
          <div class="header-left">
            <el-tag type="info">{{ currentPolicy.department }}</el-tag>
            <el-tag type="info">{{ currentPolicy.date }}</el-tag>
            <el-tag type="info">{{ currentPolicy.type }}</el-tag>
            <el-tag type="info">{{ currentPolicy.level }}</el-tag>
          </div>
          <div class="header-right">
            <el-button
              size="small"
              icon="el-icon-download"
              @click="downloadPolicy"
              >下载原文</el-button
            >
            <el-button size="small" icon="el-icon-printer" @click="printPolicy"
              >打印</el-button
            >
          </div>
        </div>

        <div class="detail-content">
          <h3 class="content-title">政策摘要</h3>
          <p class="content-summary">{{ currentPolicy.summary }}</p>

          <h3 class="content-title">政策要点</h3>
          <ul class="content-points">
            <li v-for="(point, index) in currentPolicy.keyPoints" :key="index">
              {{ point }}
            </li>
          </ul>

          <h3 class="content-title">适用范围</h3>
          <p class="content-scope">{{ currentPolicy.scope }}</p>

          <h3 class="content-title">政策全文</h3>
          <div class="content-fulltext" v-html="currentPolicy.content"></div>

          <div
            class="content-attachments"
            v-if="currentPolicy.attachments.length"
          >
            <h3 class="content-title">相关附件</h3>
            <div class="attachment-list">
              <div
                class="attachment-item"
                v-for="file in currentPolicy.attachments"
                :key="file.name"
              >
                <i class="el-icon-paperclip"></i>
                <span>{{ file.name }}</span>
                <el-button type="text" size="mini" @click="downloadFile(file)"
                  >下载</el-button
                >
              </div>
            </div>
          </div>
        </div>

        <div class="detail-footer">
          <div class="footer-tags">
            <el-tag
              v-for="tag in currentPolicy.tags"
              :key="tag"
              size="small"
              type="info"
              >{{ tag }}</el-tag
            >
          </div>
          <div class="footer-actions">
            <span class="action-item"
              ><i class="el-icon-view"></i>
              {{ currentPolicy.views }}次浏览</span
            >
            <span class="action-item"
              ><i class="el-icon-download"></i>
              {{ currentPolicy.downloads }}次下载</span
            >
            <el-button
              type="text"
              size="small"
              icon="el-icon-share"
              @click="sharePolicy"
              >分享</el-button
            >
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { scrollTop } from '@/util/scrollTop';
export default {
  name: 'PolicyModern',
  data() {
    return {
      searchQuery: '',
      hotTags: [
        '循环经济',
        '垃圾分类',
        '电子废弃物',
        '再生资源',
        '环保补贴',
        '碳中和',
        '生产者责任',
      ],
      filter: {
        type: '',
        department: '',
        dateRange: [],
      },
      sortOption: 'date',
      activeCategory: null,
      currentPage: 1,
      pageSize: 10,
      dialogVisible: false,
      currentPolicy: {
        id: '',
        title: '',
        department: '',
        date: '',
        type: '',
        level: '',
        summary: '',
        keyPoints: [],
        scope: '',
        content: '',
        tags: [],
        attachments: [],
        views: 0,
        downloads: 0,
        isNew: false,
      },
      policyTypes: [
        { value: 'law', label: '法律' },
        { value: 'regulation', label: '行政法规' },
        { value: 'department', label: '部门规章' },
        { value: 'local', label: '地方性法规' },
        { value: 'standard', label: '标准规范' },
        { value: 'notice', label: '通知公告' },
      ],
      departments: [
        { value: 'national', label: '全国人大' },
        { value: 'state', label: '国务院' },
        { value: 'ndrc', label: '国家发改委' },
        { value: 'mep', label: '生态环境部' },
        { value: 'mofcom', label: '商务部' },
        { value: 'miit', label: '工信部' },
        { value: 'local', label: '地方政府' },
      ],
      policyCategories: [
        {
          id: 'recycle',
          name: '循环经济',
          icon: 'el-icon-refresh',
          count: 28,
        },
        {
          id: 'waste',
          name: '废弃物管理',
          icon: 'el-icon-delete',
          count: 35,
        },
        {
          id: 'ewaste',
          name: '电子废弃物',
          icon: 'el-icon-cpu',
          count: 18,
        },
        {
          id: 'vehicle',
          name: '报废汽车',
          icon: 'el-icon-truck',
          count: 15,
        },
        {
          id: 'subsidy',
          name: '补贴政策',
          icon: 'el-icon-s-finance',
          count: 22,
        },
        {
          id: 'tech',
          name: '技术规范',
          icon: 'el-icon-s-data',
          count: 12,
        },
      ],
      policies: [
        {
          id: '1',
          title: '《中华人民共和国循环经济促进法》',
          department: '全国人大',
          date: '2023-06-01',
          type: '法律',
          level: '国家级',
          summary:
              '本法旨在促进循环经济发展，提高资源利用效率，保护和改善环境，实现可持续发展。',
          tags: ['循环经济', '资源利用', '可持续发展'],
          isNew: true,
          views: 1250,
          downloads: 842,
          keyPoints: [
            '确立了循环经济发展的基本管理制度',
            '规定了生产者的责任延伸制度',
            '明确了资源综合利用的激励措施',
            '强化了废弃物管理的法律责任',
          ],
          scope: '适用于中华人民共和国境内从事生产、流通、消费活动的单位和个人',
          content:
              '<p>《中华人民共和国循环经济促进法》已由中华人民共和国第十三届全国人民代表大会常务委员会第二十八次会议于2023年4月29日修订通过，现予公布，自2023年6月1日起施行。</p><p>...</p>',
          attachments: [
            { name: '循环经济促进法全文.pdf', url: '#' },
            { name: '循环经济促进法解读.docx', url: '#' },
          ],
        },
        {
          id: '2',
          title: '《废弃电器电子产品回收处理管理条例》',
          department: '国务院',
          date: '2023-03-15',
          type: '行政法规',
          level: '国家级',
          summary:
              '规范废弃电器电子产品回收处理活动，促进资源综合利用和循环经济发展，保护环境，保障人体健康。',
          tags: ['电子废弃物', '回收处理', '生产者责任'],
          isNew: false,
          views: 980,
          downloads: 653,
          keyPoints: [
            '建立了废弃电器电子产品目录管理制度',
            '实行多渠道回收和集中处理制度',
            '规定了处理企业的资格许可制度',
            '明确了生产者的回收处理责任',
          ],
          scope: '适用于中华人民共和国境内废弃电器电子产品的回收处理及相关管理活动',
          content:
              '<p>第一条 为了规范废弃电器电子产品回收处理活动，促进资源综合利用和循环经济发展，保护环境，保障人体健康，根据《中华人民共和国清洁生产促进法》、《中华人民共和国固体废物污染环境防治法》等法律，制定本条例。</p><p>...</p>',
          attachments: [{ name: '条例全文.pdf', url: '#' }],
        },
        {
          id: '3',
          title: '《再生资源回收管理办法》',
          department: '商务部',
          date: '2022-11-20',
          type: '部门规章',
          level: '部级',
          summary:
              '规范再生资源回收行业管理，促进再生资源回收，节约资源，保护环境，实现经济与社会可持续发展。',
          tags: ['再生资源', '回收管理', '行业规范'],
          isNew: false,
          views: 876,
          downloads: 542,
          keyPoints: [
            '明确了再生资源回收经营者的备案要求',
            '规范了回收站点设置标准',
            '建立了回收经营者的信用管理制度',
            '规定了再生资源回收的统计报告制度',
          ],
          scope: '适用于中华人民共和国境内从事再生资源回收经营活动的企业和个体工商户',
          content:
              '<p>第一条 为规范再生资源回收行业管理，促进再生资源回收，节约资源，保护环境，实现经济与社会可持续发展，根据有关法律、法规，制定本办法。</p><p>...</p>',
          attachments: [{ name: '管理办法全文.pdf', url: '#' }],
        },
        {
          id: '4',
          title: '《关于完善废旧家电回收处理体系推动家电更新消费的实施方案》',
          department: '国家发改委',
          date: '2023-05-12',
          type: '通知公告',
          level: '部级',
          summary:
              '通过完善回收处理体系，促进家电更新消费，推动家电行业高质量发展，培育新的经济增长点。',
          tags: ['家电回收', '以旧换新', '消费升级'],
          isNew: true,
          views: 765,
          downloads: 432,
          keyPoints: [
            '提出到2025年建成完善的废旧家电回收处理体系',
            '鼓励家电生产企业开展回收目标责任制行动',
            '支持"互联网+回收"模式创新',
            '完善废旧家电处理基金补贴制度',
          ],
          scope: '适用于家电生产、销售、回收、处理企业及相关管理部门',
          content:
              '<p>各省、自治区、直辖市及计划单列市、新疆生产建设兵团发展改革委、工业和信息化主管部门、财政厅（局）、生态环境厅（局）、住房城乡建设厅（局）、商务主管部门、市场监管局（厅、委）：</p><p>...</p>',
          attachments: [
            { name: '实施方案全文.pdf', url: '#' },
            { name: '政策解读.pptx', url: '#' },
          ],
        },
        {
          id: '5',
          title: '《新能源汽车动力蓄电池回收利用管理办法》',
          department: '工信部',
          date: '2023-02-18',
          type: '部门规章',
          level: '部级',
          summary:
              '规范新能源汽车动力蓄电池回收利用管理，保障动力蓄电池高效、环保利用，促进新能源汽车产业健康发展。',
          tags: ['新能源汽车', '动力电池', '回收利用'],
          isNew: false,
          views: 689,
          downloads: 387,
          keyPoints: [
            '建立了动力蓄电池编码制度和溯源管理平台',
            '实行生产者责任延伸制度',
            '规范了回收服务网点建设标准',
            '明确了梯次利用和再生利用要求',
          ],
          scope: '适用于中华人民共和国境内新能源汽车动力蓄电池的回收利用活动',
          content:
              '<p>第一条 为了规范新能源汽车动力蓄电池回收利用管理，保障动力蓄电池高效、环保利用，促进新能源汽车产业健康发展，依据《中华人民共和国循环经济促进法》、《中华人民共和国固体废物污染环境防治法》等法律法规，制定本办法。</p><p>...</p>',
          attachments: [{ name: '管理办法全文.pdf', url: '#' }],
        },
        {
          id: '6',
          title: '《XX市生活垃圾分类管理条例》',
          department: 'XX市人大常委会',
          date: '2023-04-01',
          type: '地方性法规',
          level: '省级',
          summary:
              '规范生活垃圾分类管理，提高生活垃圾减量化、资源化、无害化水平，促进生态文明建设。',
          tags: ['垃圾分类', '城市管理', '地方立法'],
          isNew: false,
          views: 543,
          downloads: 321,
          keyPoints: [
            '明确了生活垃圾分类标准和投放要求',
            '建立了分类收运和处理体系',
            '规定了单位和个人分类投放的责任',
            '完善了监督管理和法律责任',
          ],
          scope: '适用于XX市行政区域内生活垃圾的源头减量、分类投放、收集、运输、处理及其监督管理活动',
          content:
              '<p>第一条 为了规范生活垃圾分类管理，提高生活垃圾减量化、资源化、无害化水平，促进生态文明建设，根据《中华人民共和国固体废物污染环境防治法》等法律、法规，结合本市实际，制定本条例。</p><p>...</p>',
          attachments: [{ name: '条例全文.pdf', url: '#' }],
        },
      ],
      latestPolicies: [
        {
          id: '7',
          title: '《关于组织开展再生资源回收体系建设项目申报工作的通知》',
          date: '2023-07-15',
        },
        {
          id: '8',
          title: '《再生资源分拣中心建设管理规范》行业标准发布',
          date: '2023-07-10',
        },
        {
          id: '9',
          title: '《电子废弃物处理污染控制技术规范》修订版',
          date: '2023-06-28',
        },
        {
          id: '10',
          title: '《XX省塑料污染治理三年行动计划》',
          date: '2023-06-20',
        },
        {
          id: '11',
          title: '《关于完善废旧物资循环利用体系的意见》',
          date: '2023-06-15',
        },
      ],
      interpretations: [
        {
          id: '1',
          title: '《循环经济促进法》修订要点解读',
          summary:
              '深入解析新版循环经济促进法的主要修改内容和实施要点，帮助企业把握政策导向。',
          image: require('@/assets/recycling/policyInformation/党建.png'),
        },
        {
          id: '2',
          title: '电子废弃物回收处理政策指南',
          summary:
              '全面梳理电子废弃物回收处理相关政策法规，提供合规操作建议和案例分析。',
          image: require('@/assets/recycling/policyInformation/党建.png'),
        },
        {
          id: '3',
          title: '再生资源行业税收优惠政策解析',
          summary:
              '详细解读再生资源综合利用税收优惠政策，帮助企业充分享受政策红利。',
          image: require('@/assets/recycling/policyInformation/党建.png'),
        },
      ],
    };
  },
  computed: {
    filteredPolicies() {
      let result = [...this.policies];

      // 按分类筛选
      if (this.activeCategory) {
        const category = this.policyCategories.find(c => c.id === this.activeCategory);
        if (category) {
          result = result.filter(policy =>
              policy.tags.some(tag => category.name.includes(tag)))
        }
      }

      // 按类型筛选
      if (this.filter.type) {
        result = result.filter(policy => policy.type === this.filter.type);
      }

      // 按部门筛选
      if (this.filter.department) {
        const deptMap = {
          national: '全国人大',
          state: '国务院',
          ndrc: '国家发改委',
          mep: '生态环境部',
          mofcom: '商务部',
          miit: '工信部',
          local: '地方政府'
        };
        result = result.filter(policy =>
            policy.department === deptMap[this.filter.department]);
      }

      // 按日期筛选
      if (this.filter.dateRange && this.filter.dateRange.length === 2) {
        const [start, end] = this.filter.dateRange;
        result = result.filter(policy => {
          const policyDate = new Date(policy.date);
          const startDate = new Date(start);
          const endDate = new Date(end);
          return policyDate >= startDate && policyDate <= endDate;
        });
      }

      // 按搜索词筛选
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(policy =>
            policy.title.toLowerCase().includes(query) ||
            policy.summary.toLowerCase().includes(query) ||
            policy.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }

      // 排序
      if (this.sortOption === 'date') {
        result.sort((a, b) => new Date(b.date) - new Date(a.date));
      } else if (this.sortOption === 'hot') {
        result.sort((a, b) => b.views - a.views);
      } else if (this.sortOption === 'level') {
        const levelOrder = ['国家级', '部级', '省级', '市级'];
        result.sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level));
      }

      return result;
    },
    totalPolicies() {
      return this.filteredPolicies.length;
    },
  },
  mounted() {
    scrollTop();
  },
  methods: {
    handleSearch() {
      this.currentPage = 1;
    },
    searchByTag(tag) {
      this.searchQuery = tag;
      this.handleSearch();
    },
    resetFilters() {
      this.filter = {
        type: '',
        department: '',
        dateRange: [],
      };
      this.activeCategory = null;
      this.searchQuery = '';
      this.sortOption = 'date';
      this.currentPage = 1;
    },
    filterByCategory(categoryId) {
      this.activeCategory = this.activeCategory === categoryId ? null : categoryId;
      this.currentPage = 1;
    },
    handlePageChange(page) {
      this.currentPage = page;
      scrollTop();
    },
    showPolicyDetail(policy) {
      this.currentPolicy = policy;
      this.currentPolicy.views += 1;
      this.dialogVisible = true;
    },
    downloadPolicy() {
      this.$message.success('开始下载政策文件');
      this.currentPolicy.downloads += 1;
      // 实际下载逻辑
    },
    printPolicy() {
      this.$message.info('准备打印政策文件');
      // 实际打印逻辑
    },
    downloadFile(file) {
      this.$message.success(`开始下载 ${file.name}`);
      // 实际下载逻辑
    },
    sharePolicy() {
      this.$message.info('分享政策给好友');
      // 实际分享逻辑
    },
    viewInterpretation(item) {
      this.$message.info(`查看解读: ${item.title}`);
      // 实际跳转逻辑
    },
    backHome() {
      this.$emit('goBack', '');
    },
    getBadgeStyle(level) {
      const colors = {
        '国家级': '#F56C6C',
        '部级': '#E6A23C',
        '省级': '#409EFF',
        '市级': '#67C23A'
      };
      return {
        backgroundColor: colors[level] || '#909399'
      };
    }
  },
};
</script>

<style lang="less" scoped>
.policy-modern {
  background-color: #f8fafc;
  min-height: 100vh;
  margin-bottom: 60px;
}

.container {
  max-width: 1200px;
  padding: 0 20px;
  margin: 0 auto 30px auto;
}

.hero-section {
  color: white;
  padding: 40px 0 40px;
  position: relative;
  margin-bottom: -40px;
  background: url('~@/assets/recycling/componyImages/zaishengziyuanzhengcefagui.png')
    center;
  background-size: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 153, 255, 0.8) 0%,
    rgba(58, 194, 153, 0.6) 100%
  );
  z-index: 0;
}
.breadcrumb_span {
  color: #000000;
  font-weight: 500;
  cursor: pointer;
}
.hero-section > .container {
  position: relative;
  z-index: 1;
}
.breadcrumb /deep/ .el-breadcrumb__item {
  color: rgba(255, 255, 255, 0.8);
}

.breadcrumb /deep/ .el-breadcrumb__item:last-child {
  color: white;
  font-weight: bold;
}

.breadcrumb /deep/ .el-breadcrumb__separator {
  color: rgba(255, 255, 255, 0.6);
}

.hero-title {
  font-size: 36px;
  font-weight: 600;
  margin: 20px 0 10px;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 18px;
  opacity: 0.9;
  margin: 0 0 30px;
  font-weight: 300;
}

.hero-search {
  max-width: 800px;
  margin-top: 40px;
}

.search-input /deep/ .el-input__inner {
  height: 50px;
  border-top-left-radius: 25px;
  border-bottom-left-radius: 25px;;
  font-size: 16px;
  padding-left: 20px;
}

.search-input /deep/ .el-input-group__append {
  border-top-right-radius: 25px;
  border-bottom-right-radius: 25px;
  background: #409eff;
  border: none;
  color: white;
  padding: 0 25px;
  cursor: pointer;
}

.search-tags {
  margin-top: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.search-tags .el-tag {
  cursor: pointer;
  transition: all 0.3s;
}

.search-tags .el-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 内容布局 */
.content-layout {
  display: flex;
  gap: 24px;
  position: relative;
  z-index: 1;
}

.main-content {
  flex: 1;
  min-width: 0;
}

.sidebar {
  width: 360px;
  flex-shrink: 0;
}

@media (max-width: 992px) {
  .content-layout {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
  }
}

/* 卡片设计 */
.glassmorph {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.glassmorph:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.el-card {
  border: none;
  border-radius: 12px;
}

.el-card /deep/ .el-card__body {
  padding: 24px;
}

/* 筛选卡片 */
.filter-card {
  margin-bottom: 24px;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filter-header h2 {
  font-size: 20px;
  margin: 0;
  color: #333;
}

.reset-btn {
  color: #999;
}

.reset-btn:hover {
  color: #409eff;
}

.filter-item h3 {
  font-size: 14px;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.filter-item /deep/ .el-select,
.filter-item /deep/ .el-date-editor {
  width: 100%;
}

/* 政策列表 */
.list-card {
  margin-bottom: 30px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.list-header h2 {
  font-size: 20px;
  margin: 0;
  color: #333;
}

.sort-options {
  display: flex;
  align-items: center;
}

.sort-options span {
  font-size: 14px;
  color: #666;
  margin-right: 10px;
}

.policy-list {
  margin-top: 20px;
}

.policy-item {
  display: flex;
  padding: 20px;
  border-radius: 8px;
  background: white;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.3s;
  border-left: 4px solid transparent;
}

.policy-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-left-color: #409eff;
}

.policy-badge {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
  margin-right: 20px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.policy-content {
  flex: 1;
}

.policy-title {
  font-size: 18px;
  margin: 0 0 10px 0;
  color: #333;
  display: flex;
  align-items: center;
}

.policy-title .el-tag {
  margin-left: 10px;
}

.policy-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 10px;
}

.meta-item {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
}

.meta-item i {
  margin-right: 5px;
  color: #999;
}

.policy-summary {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.policy-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pagination {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

/* 分类卡片 */
.category-card {
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #66c2ff 0%, #3ac299 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 18px;
}

.card-header h2 {
  font-size: 20px;
  margin: 0;
  color: #333;
  flex: 1;
}

.category-list {
  display: grid;
  gap: 12px;
  h3{
    font-size: 16px;
    color: #333333;
  }
  p{
    font-size: 14px;
    color: #666666;
  }
}

.category-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}
.category-item:hover{
  background-color: #ebebee;
}
.category-icon{
  margin-right: 20px;
}
/* 最新政策卡片 */
.latest-card {
  margin-bottom: 24px;
}

.latest-list {
  display: grid;
  gap: 12px;
}

.latest-item {
  padding: 12px;
  border-radius: 6px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.3s;
}

.latest-item:hover {
  background: #e6f7ff;
  transform: translateX(4px);
}

.latest-date {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.latest-title {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 政策解读卡片 */
.interpretation-card {
  margin-bottom: 24px;
}

.interpretation-list {
  display: grid;
  gap: 16px;
}

.interpretation-item {
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #eee;
  cursor: pointer;
}

.interpretation-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.interpretation-image {
  width: 80px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.interpretation-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.interpretation-content {
  flex: 1;
}

.interpretation-content h3 {
  font-size: 15px;
  margin: 0 0 6px 0;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.interpretation-content p {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.interpretation-content .el-button {
  padding: 0;
  font-size: 12px;
}

/* 政策详情弹窗 */
.policy-dialog /deep/ .el-dialog {
  border-radius: 12px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.policy-dialog /deep/ .el-dialog__header {
  border-bottom: 1px solid #eee;
  padding: 15px 20px;
}

.policy-dialog /deep/ .el-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.policy-detail {
  padding: 20px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.header-left .el-tag {
  margin-right: 8px;
}

.detail-content {
  margin-bottom: 20px;
}

.content-title {
  font-size: 16px;
  color: #333;
  margin: 20px 0 10px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.content-summary {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  margin: 0 0 15px 0;
}

.content-points {
  margin: 0 0 15px 0;
  padding-left: 20px;
}

.content-points li {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  margin-bottom: 8px;
}

.content-scope {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  margin: 0 0 15px 0;
}

.content-fulltext {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
}

.content-fulltext p {
  margin: 0 0 15px 0;
}

.content-attachments {
  margin-top: 30px;
}

.attachment-list {
  margin-top: 10px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 4px;
  margin-bottom: 8px;
}

.attachment-item i {
  margin-right: 8px;
  color: #999;
}

.attachment-item span {
  flex: 1;
  font-size: 13px;
  color: #333;
}

.detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.footer-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.action-item {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
}

.action-item i {
  margin-right: 5px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .filter-item {
    margin-bottom: 15px;
  }

  .list-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .policy-item {
    flex-direction: column;
  }

  .policy-badge {
    width: 100%;
    height: auto;
    writing-mode: horizontal-tb;
    margin-right: 0;
    margin-bottom: 10px;
    padding: 8px;
  }

  .interpretation-item {
    flex-direction: column;
  }

  .interpretation-image {
    width: 100%;
    height: 120px;
    margin-bottom: 10px;
  }

  .policy-dialog /deep/ .el-dialog {
    width: 95% !important;
  }

  .detail-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .header-left {
    margin-bottom: 10px;
  }

  .footer-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.filter-card {
  animation: fadeIn 0.5s ease-out;
}

.list-card {
  animation: fadeIn 0.6s ease-out;
}

.category-card {
  animation: slideInUp 0.5s ease-out;
}

.latest-card {
  animation: slideInUp 0.6s ease-out;
}

.interpretation-card {
  animation: slideInUp 0.7s ease-out;
}

.policy-item {
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out;
}

.policy-item:nth-child(1) {
  animation-delay: 0.1s;
}
.policy-item:nth-child(2) {
  animation-delay: 0.2s;
}
.policy-item:nth-child(3) {
  animation-delay: 0.3s;
}
.policy-item:nth-child(4) {
  animation-delay: 0.4s;
}
.policy-item:nth-child(5) {
  animation-delay: 0.5s;
}
</style>
